import React from 'react';
import { Checkbox, FormControlLabel, Grid, Stack } from '@mui/material';

import { AddOutlined, Delete as DeleteIcon } from '@mui/icons-material';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import classes from 'new/components/settings/VendorBuilder/VendorBuilder.module.scss';
import { TextField } from 'new/elements/TextField';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { paramDataTypes } from 'new/components/settings/VendorBuilder/selectTypes';
import { Select } from 'new/elements/Select';
import { Button } from 'new/elements/Button';
import { set, has, toNumber } from 'lodash';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { DynamicConfigValues } from 'new/components/settings/VendorBuilder/DynamicConfigValues';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import Typography from 'new/elements/Typography';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';

interface AuthParamsProps {
  templateIndex: number;
}
interface ParamProps {
  templateIndex: number;
  index: number;
}

const Param = (props: ParamProps) => {
  const { index, templateIndex } = props;
  const { vendor, onChange, updateVendor, errors } = useVendorBuilder();

  const param = vendor.auth_templates[templateIndex].params[index];

  if (!param) {
    return null;
  }

  const onChangeParam = (e) => {
    const { value, name, type, checked } = e.target;
    let newValue;
    if (type === 'checkbox') {
      newValue = checked;
    } else {
      newValue = type === 'number' ? toNumber(value) : value;
    }
    updateVendor((newVendor) => {
      set(newVendor, `auth_templates[${templateIndex}].params[${index}][${name}]`, newValue);
    });
  };

  const onChangeAllowed = (e) => {
    const { checked, name } = e.target;
    updateVendor((newVendor) => {
      if (checked) {
        newVendor.auth_templates[templateIndex].params[index].allowed_values = [
          { label: '', value: '' },
        ];
      } else {
        if (has(newVendor.auth_templates[templateIndex].params[index].config, 'can_add_values')) {
          delete newVendor.auth_templates[templateIndex].params[index].config.can_add_values;
        }

        delete newVendor.auth_templates[templateIndex].params[index].allowed_values;
      }
    });
  };

  const onChangeRef = (e) => {
    const { checked, name } = e.target;
    updateVendor((newVendor) => {
      if (checked) {
        newVendor.auth_templates[templateIndex].params[index].config.param_ref = '';
      } else {
        delete newVendor.auth_templates[templateIndex].params[index].config.param_ref;
      }
    });
  };

  const onAddAllowedValue = () => {
    updateVendor((newVendor) => {
      newVendor.auth_templates[templateIndex].params[index].allowed_values.push({
        label: '',
        value: '',
      });
    });
  };

  const onRemoveParam = () => {
    updateVendor((newVendor) => {
      newVendor.auth_templates[templateIndex].params.splice(index, 1);
    });
  };

  const onChangeAllowedValue = useDebouncedCallback(
    (e) => {
      const { value, name } = e.target;
      const paramIndex = e.target.getAttribute('data-index');
      updateVendor((vendor) => {
        vendor.auth_templates[templateIndex].params[index].allowed_values[paramIndex][name] = value;
      });
    },
    [templateIndex, index],
    500,
  );

  return (
    <Stack className={classes.round_container}>
      <Section defaultExpanded title={`Auth Parameter #${index + 1}`}>
        <Stack alignItems="flex-end">
          <IconButton color="secondary" onClick={onRemoveParam}>
            <DeleteIcon />
          </IconButton>
        </Stack>
        <Stack padding="16px" gap="16px">
          <TextField
            label={
              <KnowledgePanel
                alwaysShowUnderline
                knowledgeContent={
                  <Stack padding="16px">
                    <Typography variant="h6">
                      Auth parameter Name / Macro Name(<code>param[].name</code>)
                    </Typography>
                    <Typography variant="body2">
                      Set the name of the macro to be created.
                    </Typography>
                    <Typography variant="body2">
                      <b>Note: </b>
                      <br />
                      <ol>
                        <li>
                          This has to be unique only within the scope of the auth_template it is
                          being used with.
                        </li>
                        <li>
                          For ease of database management, we are asking users to always prefix
                          these params/macros with the <code>vendor_name</code>. This is not
                          required but desirable.
                        </li>
                        <li>
                          Some properties for 3-legged oauth have special meaning for UI, so cannot
                          be renamed. Example <code>oauth2.auth_url</code>
                        </li>
                        <li>
                          This ui is autogenerating some params based on your auth type selection.
                          You don't have to use the same names. Just make sure the macro names are
                          consistent.
                        </li>
                        <li>
                          Each auth template has no idea about the existence of params for a
                          different auth template. So if you are going to use a macro in a config
                          object, make sure you create a param with same name within that
                          auth_template entry.
                        </li>
                      </ol>
                    </Typography>
                  </Stack>
                }
              >
                Param Name / Auth Template Macro Name <code>param[].name</code>
              </KnowledgePanel>
            }
            error={!!errors[`auth_templates[${templateIndex}].params[${index}].name`]}
            helperText={errors[`auth_templates[${templateIndex}].params[${index}].name`]}
            value={param.name}
            name="name"
            onChange={onChangeParam}
          />

          <TextField
            fullWidth
            label={
              <KnowledgePanel
                alwaysShowUnderline
                knowledgeContent={
                  <Stack padding="16px">
                    <Typography variant="h6">
                      Param Display Name(<code>param[].display_name</code>)
                    </Typography>
                    <Typography variant="body2">
                      User friendly name of the input field. Should be well thought out for fields
                      that are not hidden.
                    </Typography>
                  </Stack>
                }
              >
                Display Name <code>param.display_name</code>
              </KnowledgePanel>
            }
            name="display_name"
            error={!!errors[`auth_templates[${templateIndex}].params[${index}].display_name`]}
            helperText={errors[`auth_templates[${templateIndex}].params[${index}].display_name`]}
            value={param.display_name}
            onChange={onChangeParam}
          />

          <TextField
            fullWidth
            label={
              <KnowledgePanel
                alwaysShowUnderline
                knowledgeContent={
                  <Stack padding="16px">
                    <Typography variant="h6">
                      Param Description(<code>param[].description</code>)
                    </Typography>
                    <Typography variant="body2">
                      User friendly description about the parameter. This will automatically show up
                      as tooltip on the field. Leave it blank if there is no tooltip content to be
                      displayed.
                      <br />
                      <strong>Recommendation:</strong> This will feed a tooltip if not blank. So
                      make sure you set something meaningful here.
                    </Typography>
                  </Stack>
                }
              >
                Description <code>param.description</code>
              </KnowledgePanel>
            }
            name="description"
            value={param.description}
            onChange={onChangeParam}
          />

          <TextField
            fullWidth
            label={
              <KnowledgePanel
                alwaysShowUnderline
                knowledgeContent={
                  <Stack padding="16px">
                    <Typography variant="h6">
                      (Optional): Default Value of Macro <code>param.config.default</code>
                    </Typography>
                    <Typography variant="body2">
                      Set this to a reasonable value if you want to seed this input with some
                      defaults.
                      <br />
                      <strong>Note:</strong>
                      <ol>
                        <li>
                          If your param is a dropdown, you must set this to some value from the
                          dropdown choices.
                        </li>
                        <li>
                          If you don't want a default value, just remove it from the generated spec
                          before submitting on Github.
                        </li>
                      </ol>
                    </Typography>
                  </Stack>
                }
              >
                (Optional): Default Value of Macro <code>param.config.default</code>
              </KnowledgePanel>
            }
            name="config.default"
            value={param.config.default}
            onChange={onChangeParam}
          />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={
                  <KnowledgePanel
                    alwaysShowUnderline
                    knowledgeContent={
                      <Stack padding="16px">
                        <Typography variant="h6">
                          Param Display Order in UI (<code>param.order</code>)
                        </Typography>
                        <Typography variant="body2">
                          Use this to set the order in which this item will be displayed relative to
                          other parameters.
                          <br />
                          <strong>Note:</strong>
                          <ol>
                            <li>
                              Order does not have to be sequential. UI just resorts items based on
                              this field.
                            </li>
                            <li>
                              Often you might want to skip some numbers if you anticipate more props
                              that might get added later and need to be displayed.
                            </li>
                            <li>
                              We can always change this value for a template later without any risk.
                            </li>
                          </ol>
                        </Typography>
                      </Stack>
                    }
                  >
                    Param Display Order in UI <code>param.order</code>
                  </KnowledgePanel>
                }
                value={`${param.order}`}
                name="order"
                type="number"
                onChange={onChangeParam}
              />
            </Grid>
            <Grid item xs={6}>
              <Select
                fullWidth
                label={
                  <KnowledgePanel
                    alwaysShowUnderline
                    knowledgeContent={
                      <Stack padding="16px">
                        <Typography variant="h6">
                          Data Type(<code>param.data_type</code>)
                        </Typography>
                        <Typography variant="body2">
                          Set the data type of macro value. Usually you wan this to be a{' '}
                          <code>string</code>. This only impacts the type of input field that is
                          presented to user.
                        </Typography>
                      </Stack>
                    }
                  >
                    Data Type(<code>param.data_type</code>)
                  </KnowledgePanel>
                }
                value={param.data_type}
                items={paramDataTypes}
                name="data_type"
                onChange={onChangeParam}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <FormControlLabel
                onChange={onChangeRef}
                control={
                  <Checkbox defaultChecked={!!param.config.param_ref} name="config.param_ref" />
                }
                label={
                  <KnowledgePanel
                    alwaysShowUnderline
                    knowledgeContent={
                      <Stack padding="16px">
                        <Typography variant="h6">
                          Do you want this param to shadow on another?
                        </Typography>
                        <Typography variant="body2">
                          Sometimes you need some macros to shadow another macro, instead of asking
                          user to input same data multiple times. Ex: For 3-legged OAuth2, UI needs
                          different params that all reflect Client ID.
                          <br />
                          In that case, set this checkbox on for all params that need to shadow a
                          main param, then set the param name in the{' '}
                          <code>Parameter to be Referenced</code>field.
                        </Typography>
                      </Stack>
                    }
                  >
                    Do you want this param to shadow on another?
                  </KnowledgePanel>
                }
              />
            </Grid>
            <Grid item xs={8}>
              {param.config.param_ref != undefined ? (
                <TextField
                  fullWidth
                  label={
                    <KnowledgePanel
                      alwaysShowUnderline
                      knowledgeContent={
                        <Stack padding="16px">
                          <Typography variant="h6">
                            Parameter to be Referenced (<code>param.config.param_ref</code>)
                          </Typography>
                          <Typography variant="body2">
                            Set the name of the parameter that should be shadowed if this parameter
                            should be shadowing another param in this template.
                          </Typography>
                        </Stack>
                      }
                    >
                      Parameter to be Referenced (<code>param.config.param_ref</code>)
                    </KnowledgePanel>
                  }
                  defaultValue={param.config.param_ref}
                  name="config.param_ref"
                  onChange={onChangeParam}
                />
              ) : null}
            </Grid>
          </Grid>
          <Stack alignItems="center" direction="row" gap="16px">
            <FormControlLabel
              onChange={onChangeParam}
              control={<Checkbox checked={!!param.config.required} name="config.required" />}
              label={
                <KnowledgePanel
                  alwaysShowUnderline
                  knowledgeContent={
                    <Stack padding="16px">
                      <Typography variant="h6">
                        Required Field (<code>param.config.required</code>)?
                      </Typography>
                      <Typography variant="body2">
                        Set this if the field should show up as a required field to user.
                        <br />
                        <strong>Note:</strong> This setting mainly acts as a UI validation for
                        better user feedback. The API macro system needs all macros in{' '}
                        <code>auth_template.config</code> to be present in payload, so it is not
                        like you can skip a macro in the payload. Right now in essence everything is
                        required, but API will get smarter in future.
                      </Typography>
                    </Stack>
                  }
                >
                  Required Field? <code>param.config.required</code>
                </KnowledgePanel>
              }
            />

            <FormControlLabel
              onChange={onChangeParam}
              control={<Checkbox checked={!!param.secured} name="secured" />}
              label={
                <KnowledgePanel
                  alwaysShowUnderline
                  knowledgeContent={
                    <Stack padding="16px">
                      <Typography variant="h6">
                        Secure Field (<code>param.secured</code>)?
                      </Typography>
                      <Typography variant="body2">
                        By default, we want anything about credentials to be encrypted. But
                        sometimes we need to display some params back to the user when looking at
                        the credential. For ex: username is useful for understanding context, while
                        password should never be decrypted.
                        <br />
                        <strong>Recommendation:</strong> Set this to <code>true</code> or{' '}
                        <code>false</code> based on whether the API should display those values back
                        when fetching the credential. This can be modified later also without any
                        risk.
                      </Typography>
                    </Stack>
                  }
                >
                  Secure Field? <code>param.secured</code>
                </KnowledgePanel>
              }
            />

            <FormControlLabel
              onChange={onChangeParam}
              control={
                <Checkbox checked={!!param.config.isHiddenField} name="config.isHiddenField" />
              }
              label={
                <KnowledgePanel
                  alwaysShowUnderline
                  knowledgeContent={
                    <Stack padding="16px">
                      <Typography variant="h6">
                        Hiden Field (<code>param.config.isHiddenField</code>)?
                      </Typography>
                      <Typography variant="body2">
                        Sometimes you need UI to use a constant macro but don't want the user to see
                        it. Set this optional field if you need to hide the input from UI.
                      </Typography>
                    </Stack>
                  }
                >
                  Hidden Field? <code>param.config.isHiddenField</code>
                </KnowledgePanel>
              }
            />
          </Stack>
          <FormControlLabel
            onChange={onChangeAllowed}
            control={<Checkbox checked={!!param.allowed_values} />}
            label={
              <KnowledgePanel
                alwaysShowUnderline
                knowledgeContent={
                  <Stack padding="16px">
                    <Typography variant="h6">Display as Dropdown?</Typography>
                    <Typography variant="body2">
                      Any input field can be displayed as a dropdown of choices. Select this option
                      and configure related properties.
                    </Typography>
                  </Stack>
                }
              >
                Show as Dropdown?
              </KnowledgePanel>
            }
          />

          {param.allowed_values?.length ? (
            <>
              <FormControlLabel
                onChange={onChangeParam}
                control={
                  <Checkbox checked={!!param.config.can_add_values} name="config.can_add_values" />
                }
                label={
                  <KnowledgePanel
                    alwaysShowUnderline
                    knowledgeContent={
                      <Stack padding="16px">
                        <Typography variant="h6">
                          Allow user to enhance dropdown? <code>param.config.can_add_values</code> ?
                        </Typography>
                        <Typography variant="body2">
                          Typically you do not want to list all choices in dropdown and want user to
                          be able to set their own value that might not be present in preset
                          choices. Set this property to true for allowing that control.
                          <br />
                          <strong>Recommendation:</strong> Always set it to true if you are choosing
                          dropdowns, unless you are sure there should be no user modification
                          allowed.
                        </Typography>
                      </Stack>
                    }
                  >
                    Allow user to enhance dropdown? <code>param.config.can_add_values</code>
                  </KnowledgePanel>
                }
              />

              <Section
                title={
                  <KnowledgePanel
                    alwaysShowUnderline
                    knowledgeContent={
                      <Stack padding="16px">
                        <Typography variant="h6">
                          Allowed Dropdown Choices <code>param.allowed_values</code>
                        </Typography>
                        <Typography variant="body2">
                          Add a list of choices that should be displayed to user.
                          <br />
                          Each entry takes requires a <code>label</code> and a <code>value</code>{' '}
                          where:
                          <ul>
                            <li>
                              <code>label</code>: What the user will see in the dropdown choice.
                              Labels are only for display and can be different from what is needed
                              in API calls.
                            </li>
                            <li>
                              <code>value</code>: Actual macro value that will be used if user
                              selects this option.
                            </li>
                            <li>
                              <code>description</code>:
                            </li>{' '}
                            This is an optional field for any item in the{' '}
                            <code>allowed_values</code> array. If an entry has{' '}
                            <code>description</code>, the content will be displayed as additional
                            help information beloe the label. If you want to set this property, you
                            can do it by modifying the generated template while submitting to
                            GitHub.
                          </ul>
                        </Typography>
                      </Stack>
                    }
                  >
                    Allowed Dropdown Choices <code>param.allowed_values</code>
                  </KnowledgePanel>
                }
                gap="16px"
              >
                {param.allowed_values?.map((allowed, index) => {
                  return (
                    <Stack gap="16px">
                      <Grid container spacing={4}>
                        <Grid item xs={5}>
                          <TextField
                            label={
                              <>
                                Label <code>allowed_values[].label</code>
                              </>
                            }
                            inputProps={{
                              'data-index': index,
                            }}
                            name="label"
                            defaultValue={allowed.label}
                            onChange={onChangeAllowedValue}
                          />
                        </Grid>
                        <Grid item xs={7}>
                          <TextField
                            fullWidth
                            label={
                              <>
                                Value <code>allowed_values[].value</code>
                              </>
                            }
                            inputProps={{
                              'data-index': index,
                            }}
                            name="value"
                            defaultValue={allowed.value}
                            onChange={onChangeAllowedValue}
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        fullWidth
                        label={
                          <>
                            Description <code>allowed_values[].description</code>
                          </>
                        }
                        inputProps={{
                          'data-index': index,
                        }}
                        name="description"
                        defaultValue={allowed.value}
                        onChange={onChangeAllowedValue}
                      />
                    </Stack>
                  );
                })}
                <Stack alignItems="flex-start">
                  <Button onClick={onAddAllowedValue} startIcon={<AddOutlined />}>
                    Add allowed value
                  </Button>
                </Stack>
              </Section>
            </>
          ) : null}
          <DynamicConfigValues path={`auth_templates[${templateIndex}].params[${index}].config`} />
        </Stack>
      </Section>
    </Stack>
  );
};

export const AuthParams = (props: AuthParamsProps) => {
  const { templateIndex } = props;
  const { vendor, updateVendor, errors } = useVendorBuilder();

  const params = vendor.auth_templates[templateIndex].params;

  const onAddNewParam = () => {
    updateVendor((newVendor) => {
      const params = newVendor.auth_templates[templateIndex].params;
      const order = params.length + 1;
      params.push({
        name: vendor.name + '.',
        display_name: '',
        description: '',
        order,
        data_type: 'string',
        config: {
          default: '',
        },
      });
    });
  };

  return (
    <Section
      title={
        <KnowledgePanel
          alwaysShowUnderline
          knowledgeContent={
            <div style={{ padding: 'var(--spacing-lg)' }}>
              <Typography variant="body2">
                <strong>Authentication Parameters</strong>
                <br />
                Define authentication parameters that the UI screen needs to be aware of. You can
                then reference these properties as macros when setting values of keys in the{' '}
                <code>auth_template[].config</code> object.
                <br />. When the UI sends a request to API, this equation is applied by API: UI
                payload with: <code>template_config.[param_name]:value</code> + vendor entry in
                database for <code>auth_template[].config</code> = generated credential config where
                API has replaced macros in <code>auth_template[].config</code> with corresponding
                values in payload.
                <br />
                <strong>Note:</strong>
                <ol>
                  <li>
                    Most of the params you are adding will be visible to users, but sometimes UI
                    needs extra hidden constants (for ex: 3-legged oauth)
                  </li>
                </ol>
              </Typography>
            </div>
          }
        >
          <div>
            Template #{templateIndex + 1}: Auth Parameters (Current Count:
            {params.length}) <br /> [<code>auth_templates[].params</code>]
          </div>
        </KnowledgePanel>
      }
      gap="16px"
    >
      {params?.map((param, index) => {
        return <Param templateIndex={templateIndex} index={index} key={index} />;
      })}
      <Stack alignItems="flex-start">
        <Button endIcon={<AddOutlined />} onClick={onAddNewParam}>
          Add New Auth Parameter to Template #{templateIndex + 1}
        </Button>
      </Stack>
    </Section>
  );
};
